import { ReactNode, useState } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card"
import { Badge } from "../components/ui/badge"
import { Button } from "../components/ui/button"
import { Head } from "@inertiajs/react"
import { ExternalLink, Filter } from "lucide-react"
import { siGithub } from 'simple-icons'
import Layout from "../layouts/Layout"
import { Project, PaginationData } from "../types/project"
import { usePage, router } from "@inertiajs/react"
import Preloader from "../components/ui/preloader"
import DataTableResults from "../components/ui/data-table-results"
import DataTablePagination from "../components/ui/data-table-pagination"

declare module "@inertiajs/core" {
  interface PageProps {
    projects: Project[]
    project_pagination: PaginationData
  }
}


const Projects = () => {
  const { projects, project_pagination } = usePage().props
  const [isSearching, setIsSearching] = useState(false)
  const [filter, setFilter] = useState("all")

  // GitHub icon component
  const GithubIcon = ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d={siGithub.path} />
    </svg>
  )

  const categories = [
    { key: "all", label: "All Projects" },
    { key: "rails", label: "Rails Apps" },
    { key: "ai", label: "AI Projects" },
    { key: "domain", label: "Domain Tools" }
  ]

  // const filteredProjects = filter === "all" 
  //   ? projects 
  //   : projects.filter(project => project.category === filter);

  // const featuredProjects = filteredProjects.filter(project => project.featured);
  // const regularProjects = filteredProjects.filter(project => !project.featured);

  const featuredProjects = projects.filter(project => project.featured)
  const regularProjects = projects.filter(project => !project.featured)

  const getStatusColor = (status: string) => {
    switch (status) {
      case "In Production": return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
      case "Active Development": return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
      case "Open Source": return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300";
      case "Prototype": return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300";
      case "Maintenance": return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300";
      default: return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300";
    }
  }

  const handlePageChange = (page: number) => {
    setIsSearching(true)
    router.get('/projects', { page }, {
      preserveState: true,
      preserveScroll: true,
      onFinish: () => setIsSearching(false)
    })
  }

  return (
    <>
      <Head title="Codeware - Projects" >
        <meta name="description" content="A collection of Rails applications, AI experiments, and domain industry tools I've built or contributed to over the years." />
      </Head>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="max-w-4xl mx-auto text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">Projects</h1>
          <p className="text-xl text-muted-foreground mb-8">
            A collection of Rails applications, AI experiments, and domain industry tools I've built or contributed to over the years.
          </p>
        </div>

        {/* Filter
        <div className="max-w-4xl mx-auto mb-12">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium text-muted-foreground">Filter by category:</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <Button
                key={category.key}
                variant={filter === category.key ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter(category.key)}
              >
                {category.label}
              </Button>
            ))}
          </div>
        </div> */}

        <Preloader isLoading={isSearching}>

          {/* Search Results Info */}
          {project_pagination && projects.length > 0 && <DataTableResults pagination={project_pagination} object_name="projects" />}

          {/* Featured Projects */}
          {featuredProjects.length > 0 && (
            <section className="mb-16">
              <h2 className="text-2xl font-bold mb-8">Featured Projects</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {featuredProjects.map((project) => (
                  <Card key={project.id} className="hover:shadow-lg transition-shadow border-primary/20">
                    <div className="aspect-video bg-muted rounded-t-lg flex items-center justify-center overflow-hidden">
                      <img
                        src={project.image?.attached?.[0]?.url || project.image_url || 'placeholder.png'} 
                        alt={project.title}
                        className="w-full h-full object-contain rounded-t-lg"
                      />
                    </div>
                    <CardHeader>
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant="default" className="bg-primary/10 text-primary border-primary/20">
                          Featured
                        </Badge>
                        <Badge className={getStatusColor(project.status)}>
                          {project.status}
                        </Badge>
                      </div>
                      <CardTitle className="text-xl">{project.title}</CardTitle>
                      <CardDescription className="text-base">{project.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {project.tech_stack.split(',').map((tech) => (
                          <Badge key={tech} variant="secondary" className="text-xs">
                            {tech}
                          </Badge>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        {project.demo_url && (
                          <Button size="sm" asChild>
                            <a href={project.demo_url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="mr-2 h-4 w-4" />
                              Live Demo
                            </a>
                          </Button>
                        )}
                        {project.github_url && (
                          <Button size="sm" variant="outline" asChild>
                            <a href={project.github_url} target="_blank" rel="noopener noreferrer">
                              <GithubIcon className="mr-2 h-4 w-4" />
                              Source Code
                            </a>
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          )}

          {/* All Projects */}
          <section>
            <h2 className="text-2xl font-bold mb-8">All Projects</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {regularProjects.map((project) => (
                <Card key={project.id} className="hover:shadow-lg transition-shadow">
                  <div className="aspect-video bg-muted rounded-t-lg flex items-center justify-center overflow-hidden">
                    <img 
                      src={project.image?.attached?.[0]?.url || project.image_url || 'placeholder.png'} 
                      alt={project.title}
                      className="w-full h-full object-contain rounded-t-lg"
                    />
                  </div>
                  <CardHeader>
                    <div className="flex items-center justify-between mb-2">
                      <Badge className={getStatusColor(project.status)}>
                        {project.status}
                      </Badge>
                    </div>
                    <CardTitle className="text-lg">{project.title}</CardTitle>
                    <CardDescription className="text-sm line-clamp-3">
                      {project.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-1 mb-4">
                      {project.tech_stack.split(',').slice(0, 3).map((tech) => (
                        <Badge key={tech} variant="secondary" className="text-xs">
                          {tech}
                        </Badge>
                      ))}
                      {project.tech_stack.split(',').length > 3 && (
                        <Badge variant="secondary" className="text-xs">
                          +{project.tech_stack.split(',').length - 3} more
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {project.demo_url && (
                        <Button size="sm" asChild>
                          <a href={project.demo_url} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="mr-1 h-3 w-3" />
                            Demo
                          </a>
                        </Button>
                      )}
                      {project.github_url && (
                        <Button size="sm" variant="outline" asChild>
                          <a href={project.github_url} target="_blank" rel="noopener noreferrer">
                            <GithubIcon className="mr-1 h-3 w-3" />
                            Code
                          </a>
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {projects.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No projects found in this category.</p>
            </div>
          )}

          {/* Pagination */}
          {project_pagination && (
            <DataTablePagination 
              pagination={project_pagination} 
              onPageChange={handlePageChange}
            />
          )}

        </Preloader>
      </div>
    </>
  )
}

Projects.layout = (page: ReactNode) => <Layout>{page}</Layout>

export default Projects